/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { yupResolver } from '@hookform/resolvers/yup';
import { Modal, TextInput, Loading } from 'carbon-components-react';
import _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { sharedMessages } from '@ibm-watson/shared/modules/channels/components/messages';
import { ReadOnlyNotification } from '@ibm-watson/shared/modules/ia/components/access-control/ReadOnlyNotification';
import { schema } from '@ibm-watson/shared/modules/ia/util/validation/delete-modal/schema';
import { RootState } from '@ibm-watson/shared/ts/state';
import { useFormHelpers } from '../../hooks/useFormHelpers';
import { deleteIntegration } from '../../store/channels/actions';
import { deleteIntegration as deleteClassicIntegration } from '../../store/channels/classic/actions';
import { selectIntegrationById } from '../../store/channels/selectors';
import {
  detachServiceDeskFromIntegrations,
  resetServiceDesk
} from '../../store/service-desks/actions';
import { channelTypeToChannelName } from '../../util/channelTypes';
import { FIELDS } from '../web-chat/schema';
import { messages } from './messages';

import './ServiceDeskResetModal.scss';

interface ServiceDeskResetModalProps {
  assistantId: string;
  environmentId?: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  readOnly?: boolean;
  serviceDeskId: string;
}

export const ServiceDeskResetModal = React.memo(
  (props: ServiceDeskResetModalProps) => {
    const {
      assistantId,
      environmentId,
      isOpen,
      onClose,
      serviceDeskId,
      readOnly = true
    } = props;

    const intl = useIntl();
    const dispatch = useDispatch();

    const formMethods = useFormContext();
    const { updateFieldValue } = useFormHelpers(formMethods);

    const serviceDesk = useSelector((state: RootState) =>
      selectIntegrationById(state, serviceDeskId)
    );
    const serviceDeskName = channelTypeToChannelName[serviceDesk?.type];
    const {
      control,
      handleSubmit,
      setValue,
      formState: { isValid, isSubmitting }
    } = useForm({
      mode: 'onChange',
      resolver: yupResolver(schema)
    });

    useEffect(() => {
      setValue('delete', '');
    }, [isOpen, setValue]);

    const handleDeleteRequest = useCallback(async () => {
      try {
        updateFieldValue(FIELDS.SERVICE_DESK.IS_ON, false);
        updateFieldValue(FIELDS.SERVICE_DESK.INTEGRATION_ID, '');
        updateFieldValue(FIELDS.SERVICE_DESK.INTEGRATION_TYPE, '');
        // First find any/all integrations that have this service
        // desk attached, and remove the service desk inforation from
        // that integration.
        await dispatch(
          detachServiceDeskFromIntegrations({
            assistantId,
            environmentId,
            serviceDeskId
          })
        );
        // Then delete the service desk integration.
        await dispatch(
          !_.isNil(environmentId)
            ? await dispatch(
                deleteIntegration({
                  assistantId,
                  environmentId,
                  integrationId: serviceDeskId
                })
              )
            : await dispatch(
                deleteClassicIntegration({
                  assistantId,
                  integrationId: serviceDeskId
                })
              )
        );
        // Finally, remove the current/draft service desk.
        await dispatch(resetServiceDesk());

        onClose();
      } catch (e) {
        console.error(e);
      }
    }, [
      assistantId,
      dispatch,
      environmentId,
      onClose,
      serviceDeskId,
      updateFieldValue
    ]);

    return ReactDOM.createPortal(
      <Modal
        data-testid={'reset-service-desk-modal'}
        className="ServiceDeskResetModal"
        modalHeading={<FormattedMessage {...messages.resetModalHeading} />}
        open={isOpen}
        primaryButtonText={<FormattedMessage {...sharedMessages.delete} />}
        secondaryButtonText={<FormattedMessage {...sharedMessages.cancel} />}
        onRequestSubmit={handleSubmit(handleDeleteRequest)}
        onRequestClose={onClose}
        primaryButtonDisabled={!isValid || isSubmitting}
        danger>
        <div className="ServiceDeskResetModal__body">
          <ReadOnlyNotification readOnly={readOnly} />
          <FormattedMessage
            {...messages.resetModalBody}
            values={{ serviceDeskName }}
          />
          {isSubmitting && <Loading withOverlay />}
        </div>
        <div className="ServiceDeskResetModal__input">
          <Controller
            name="delete"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                readOnly={readOnly}
                id="ServiceDeskResetModal-input-text"
                {...field}
                data-testid={'modal-confirmation-input'}
                labelText={
                  <FormattedMessage {...messages.deleteInputLabelText} />
                }
                placeholder={intl.formatMessage(
                  messages.deleteInputPlaceholderText
                )}
                disabled={isSubmitting || readOnly}
              />
            )}
          />
        </div>
      </Modal>,
      document.body
    );
  }
);
